/**
 * useRealtimeOrders - Real-time order execution updates via WebSocket
 * Streams order status changes, fills, and execution events
 */

'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useWebSocket } from '@/domains/market-data/hooks/useWebSocket';

// =============================================================================
// TYPES
// =============================================================================

export interface RealtimeOrder {
  orderId: string;
  symbol: string;
  side: 'buy' | 'sell';
  orderType: 'market' | 'limit' | 'stop' | 'stop_limit';
  status: 'pending' | 'working' | 'partially_filled' | 'filled' | 'cancelled' | 'rejected' | 'expired';
  quantity: number;
  filledQuantity: number;
  remainingQuantity: number;
  price?: number;
  averageFillPrice?: number;
  timestamp: string;
}

export interface RealtimeFill {
  fillId: string;
  orderId: string;
  symbol: string;
  side: 'buy' | 'sell';
  quantity: number;
  price: number;
  commission: number;
  venue: string;
  timestamp: string;
}

export interface RealtimeExecution {
  executionId: string;
  orderId: string;
  type: 'new' | 'partial_fill' | 'fill' | 'cancel' | 'reject' | 'modify';
  symbol: string;
  quantity?: number;
  price?: number;
  message: string;
  timestamp: string;
}

export interface OrderUpdateEvent {
  type: 'order_update' | 'fill' | 'execution';
  data: RealtimeOrder | RealtimeFill | RealtimeExecution;
  timestamp: string;
}

export interface UseRealtimeOrdersConfig {
  /** Auto-connect on mount */
  autoConnect?: boolean;
  /** Maximum events to buffer */
  maxEvents?: number;
  /** Filter by symbols */
  symbols?: string[];
  /** Callback on order update */
  onOrderUpdate?: (order: RealtimeOrder) => void;
  /** Callback on fill */
  onFill?: (fill: RealtimeFill) => void;
  /** Callback on execution */
  onExecution?: (execution: RealtimeExecution) => void;
}

// =============================================================================
// HOOK
// =============================================================================

export const useRealtimeOrders = (config: UseRealtimeOrdersConfig = {}) => {
  const {
    autoConnect = true,
    maxEvents = 200,
    symbols,
    onOrderUpdate,
    onFill,
    onExecution,
  } = config;

  const [orders, setOrders] = useState<Map<string, RealtimeOrder>>(new Map());
  const [recentFills, setRecentFills] = useState<RealtimeFill[]>([]);
  const [recentExecutions, setRecentExecutions] = useState<RealtimeExecution[]>([]);
  const [eventCount, setEventCount] = useState(0);

  const callbacksRef = useRef({ onOrderUpdate, onFill, onExecution });
  callbacksRef.current = { onOrderUpdate, onFill, onExecution };

  const handleMessage = useCallback((data: any) => {
    if (data.type === 'pong' || data.type === 'ping') return;

    // Filter by symbols if configured
    if (symbols?.length && data.data?.symbol && !symbols.includes(data.data.symbol)) {
      return;
    }

    setEventCount((prev: number) => prev + 1);

    switch (data.type) {
      case 'order_update': {
        const order = data.data as RealtimeOrder;
        setOrders((prev: Map<string, RealtimeOrder>) => {
          const updated = new Map(prev);
          updated.set(order.orderId, order);
          return updated;
        });
        callbacksRef.current.onOrderUpdate?.(order);
        break;
      }

      case 'fill': {
        const fill = data.data as RealtimeFill;
        setRecentFills((prev: RealtimeFill[]) => [fill, ...prev].slice(0, maxEvents));
        callbacksRef.current.onFill?.(fill);
        break;
      }

      case 'execution': {
        const execution = data.data as RealtimeExecution;
        setRecentExecutions((prev: RealtimeExecution[]) => [execution, ...prev].slice(0, maxEvents));
        callbacksRef.current.onExecution?.(execution);
        break;
      }
    }
  }, [symbols, maxEvents]);

  // Private channel: mint short-lived ticket on every connect/reconnect
  const { state, send, connect, disconnect, subscribe } = useWebSocket(
    {
      channel: 'orders',
      useTicket: true,
      requireTicket: true,
      softFailAuth: false,
      reconnect: { enabled: true, maxAttempts: 10, delay: 1000, backoffMultiplier: 1.5 },
      heartbeat: { enabled: true, interval: 30000, message: { type: 'ping' } },
    },
    handleMessage
  );

  // Auto-connect
  useEffect(() => {
    if (autoConnect) {
      connect();
    }
    return () => disconnect();
  }, [autoConnect, connect, disconnect]);

  // Subscribe to channels when connected
  useEffect(() => {
    if (state.status === 'connected') {
      subscribe('order_update', () => { });
      subscribe('fill', () => { });
      subscribe('execution', () => { });

      // Send subscription message for symbols
      if (symbols?.length) {
        send({ type: 'subscribe', channels: ['executions', 'fills'], symbols });
      } else {
        send({ type: 'subscribe', channels: ['executions', 'fills'] });
      }
    }
  }, [state.status, subscribe, send, symbols]);

  // Derived state
  const ordersArray = Array.from(orders.values()) as RealtimeOrder[];
  const activeOrders = ordersArray.filter(
    (o: RealtimeOrder) => ['pending', 'working', 'partially_filled'].includes(o.status)
  );

  const completedOrders = ordersArray.filter(
    (o: RealtimeOrder) => ['filled', 'cancelled', 'rejected', 'expired'].includes(o.status)
  );

  const clearHistory = useCallback(() => {
    setRecentFills([]);
    setRecentExecutions([]);
    setEventCount(0);
  }, []);

  return {
    // Connection
    connectionStatus: state.status,
    isConnected: state.status === 'connected',
    connect,
    disconnect,

    // Data
    orders: ordersArray,
    activeOrders,
    completedOrders,
    recentFills,
    recentExecutions,
    eventCount,

    // Actions
    clearHistory,
    send,
  };
};

export default useRealtimeOrders;
